import {BUILDING_IMPACT_TEXTURE_KEYS} from '../../configs/buildingConstructionAnimation';
import {CELL_OUTLINE_RADIUS_PX, CELL_OUTLINE_TEXTURE_SIZE} from './cellOutlineTextures';
import {hexDistance} from './sizeRecruitmentTextures';

/**
 * Текстуры строительства (см. configs/buildingConstructionAnimation): трещины от
 * удара, лучи, столб света и жар ячейки. Все белые, цвет дает tint. Считаются
 * лениво при первой постройке и лежат в памяти, а не на canvas: canvas теряется
 * при потере контекста видеокарты.
 */

const clamp01 = (value) => Math.min(1, Math.max(0, value));

export const BUILDING_IMPACT_TEXTURE_SIZES = {
  [BUILDING_IMPACT_TEXTURE_KEYS.CRACKS]: {width: 192, height: 192},
  [BUILDING_IMPACT_TEXTURE_KEYS.RAYS]: {width: 256, height: 256},
  [BUILDING_IMPACT_TEXTURE_KEYS.BEAM]: {width: 64, height: 256},
  [BUILDING_IMPACT_TEXTURE_KEYS.ZONE_HEAT]: {width: CELL_OUTLINE_TEXTURE_SIZE, height: CELL_OUTLINE_TEXTURE_SIZE},
};

const buildWhitePixels = (key, resolveAlpha) => {
  const {width, height} = BUILDING_IMPACT_TEXTURE_SIZES[key];
  const pixels = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      pixels[offset] = 255;
      pixels[offset + 1] = 255;
      pixels[offset + 2] = 255;
      pixels[offset + 3] = Math.round(255 * clamp01(resolveAlpha(x + 0.5 - width / 2, y + 0.5 - height / 2)));
    }
  }
  return pixels;
};

// Свой генератор: рисунок трещин одинаков от запуска к запуску и не трогает
// Math.random, а разнообразие дает поворот картинки
const createSeededRandom = (seed) => {
  let value = seed;
  return () => {
    value = (value * 1664525 + 1013904223) % 4294967296;
    return value / 4294967296;
  };
};

const CRACK_BRANCHES = 9;
const CRACK_STEPS = 6;
const CRACK_GLOW_WIDTH = 4.5;

/** Ломаные трещин от центра: главные и ответвления, толщина сходит на нет к концу */
export const buildCrackSegments = () => {
  const {width} = BUILDING_IMPACT_TEXTURE_SIZES[BUILDING_IMPACT_TEXTURE_KEYS.CRACKS];
  const maxRadius = width / 2 - CRACK_GLOW_WIDTH - 2;
  const random = createSeededRandom(7341);
  const segments = [];

  const growCrack = (startX, startY, startAngle, length, startWidth, canBranch) => {
    let x = startX;
    let y = startY;
    let angle = startAngle;
    for (let step = 0; step < CRACK_STEPS; step += 1) {
      angle += (random() - 0.5) * 0.9;
      const stepLength = length / CRACK_STEPS;
      const toX = x + Math.cos(angle) * stepLength;
      const toY = y + Math.sin(angle) * stepLength;
      const taper = 1 - step / CRACK_STEPS;
      if (Math.hypot(toX, toY) < maxRadius) {
        segments.push({fromX: x, fromY: y, toX, toY, halfWidth: Math.max(0.5, startWidth * taper)});
      }
      if (canBranch && step > 0 && random() < 0.45) {
        growCrack(toX, toY, angle + (random() < 0.5 ? -1 : 1) * (0.5 + random() * 0.6), length * (0.28 + random() * 0.2), startWidth * taper * 0.6, false);
      }
      x = toX;
      y = toY;
    }
  };

  for (let branch = 0; branch < CRACK_BRANCHES; branch += 1) {
    const angle = (branch + (random() - 0.5) * 0.7) * Math.PI * 2 / CRACK_BRANCHES;
    const start = maxRadius * 0.13;
    growCrack(Math.cos(angle) * start, Math.sin(angle) * start, angle, maxRadius * (0.62 + random() * 0.38), 2.4, true);
  }
  return segments;
};

const distanceToSegment = (px, py, {fromX, fromY, toX, toY}) => {
  const deltaX = toX - fromX;
  const deltaY = toY - fromY;
  const share = clamp01(((px - fromX) * deltaX + (py - fromY) * deltaY) / (deltaX * deltaX + deltaY * deltaY || 1));
  return Math.hypot(px - fromX - deltaX * share, py - fromY - deltaY * share);
};

/** Трещины: плотная линия и мягкое свечение вокруг нее */
export const buildCracksPixels = () => {
  const key = BUILDING_IMPACT_TEXTURE_KEYS.CRACKS;
  const {width, height} = BUILDING_IMPACT_TEXTURE_SIZES[key];
  const alphas = new Float32Array(width * height);

  // Только в рамке отрезка: точек много, а трещин мало
  buildCrackSegments().forEach((segment) => {
    const reach = segment.halfWidth + CRACK_GLOW_WIDTH + 1;
    const left = Math.max(0, Math.floor(Math.min(segment.fromX, segment.toX) + width / 2 - reach));
    const right = Math.min(width - 1, Math.ceil(Math.max(segment.fromX, segment.toX) + width / 2 + reach));
    const top = Math.max(0, Math.floor(Math.min(segment.fromY, segment.toY) + height / 2 - reach));
    const bottom = Math.min(height - 1, Math.ceil(Math.max(segment.fromY, segment.toY) + height / 2 + reach));
    for (let y = top; y <= bottom; y += 1) {
      for (let x = left; x <= right; x += 1) {
        const distance = distanceToSegment(x + 0.5 - width / 2, y + 0.5 - height / 2, segment);
        const core = clamp01(segment.halfWidth + 0.5 - distance);
        const glow = 0.5 * clamp01(1 - (distance - segment.halfWidth) / CRACK_GLOW_WIDTH) ** 2;
        const index = y * width + x;
        alphas[index] = Math.max(alphas[index], core, glow);
      }
    }
  });

  return buildWhitePixels(key, (px, py) => alphas[Math.floor(py + height / 2) * width + Math.floor(px + width / 2)]);
};

const RAY_COUNT = 14;

/** Лучи: узкие клинья разной длины и ширины, плотные у центра */
export const buildRaysPixels = () => {
  const key = BUILDING_IMPACT_TEXTURE_KEYS.RAYS;
  const {width} = BUILDING_IMPACT_TEXTURE_SIZES[key];
  const radius = width / 2;
  const random = createSeededRandom(2207);
  const rays = Array.from({length: RAY_COUNT}, (unused, index) => ({
    angle: (index + (random() - 0.5) * 0.5) * Math.PI * 2 / RAY_COUNT,
    halfWidth: 0.07 + random() * 0.09,
    reach: index % 2 === 0 ? 0.88 + random() * 0.12 : 0.5 + random() * 0.26,
  }));

  return buildWhitePixels(key, (px, py) => {
    const distance = Math.hypot(px, py) / radius;
    if (distance >= 1) {
      return 0;
    }
    const angle = Math.atan2(py, px);
    let strength = 0;
    rays.forEach((ray) => {
      const raw = Math.abs(angle - ray.angle) % (Math.PI * 2);
      const delta = raw > Math.PI ? Math.PI * 2 - raw : raw;
      const across = clamp01(1 - delta / ray.halfWidth);
      const along = clamp01(1 - distance / ray.reach);
      strength = Math.max(strength, across ** 1.3 * along ** 0.9);
    });
    return strength;
  });
};

/** Столб света: основание внизу плотное, вверх растворяется, края мягкие */
export const buildBeamPixels = () => {
  const key = BUILDING_IMPACT_TEXTURE_KEYS.BEAM;
  const {width, height} = BUILDING_IMPACT_TEXTURE_SIZES[key];
  return buildWhitePixels(key, (px, py) => {
    const across = clamp01(1 - Math.abs(px) / (width / 2));
    // 0 — верх картинки, 1 — низ
    const along = clamp01(py / height + 0.5);
    const bottomFade = clamp01((1 - along) / 0.05);
    return across ** 1.7 * along ** 1.3 * bottomFade;
  });
};

const HEAT_CENTER_ALPHA = 0.42;
const HEAT_EDGE_POWER = 2.2;

/** Жар в ячейке: заливка шестиугольником, у кромки плотнее, чем в середине */
export const buildZoneHeatPixels = () => {
  const apothem = CELL_OUTLINE_RADIUS_PX * Math.cos(Math.PI / 6);
  return buildWhitePixels(BUILDING_IMPACT_TEXTURE_KEYS.ZONE_HEAT, (px, py) => {
    const distance = hexDistance(px, py, apothem);
    if (distance > 0.5) {
      return 0;
    }
    const edge = clamp01(1 + distance / apothem);
    return clamp01(0.5 - distance) * (HEAT_CENTER_ALPHA + (1 - HEAT_CENTER_ALPHA) * edge ** HEAT_EDGE_POWER);
  });
};

const PIXEL_BUILDERS = {
  [BUILDING_IMPACT_TEXTURE_KEYS.CRACKS]: buildCracksPixels,
  [BUILDING_IMPACT_TEXTURE_KEYS.RAYS]: buildRaysPixels,
  [BUILDING_IMPACT_TEXTURE_KEYS.BEAM]: buildBeamPixels,
  [BUILDING_IMPACT_TEXTURE_KEYS.ZONE_HEAT]: buildZoneHeatPixels,
};

/**
 * Зарегистрировать текстуры в сцене (один раз)
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR
 * @returns {boolean} готовы ли текстуры
 */
export const ensureBuildingImpactTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(BUILDING_IMPACT_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = BUILDING_IMPACT_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
